/**
 * Mandatory username prompt for social sign-ins.
 *
 * Google/GitHub accounts arrive with profiles.username = NULL. A username is
 * required for account-backed features (sign-in by username, saved routes,
 * moderation), so signed-in users without one are blocked by this dialog until
 * they pick a unique one. There is deliberately no way to dismiss it — the
 * only exits are completing the choice or signing out.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { User, Check, AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/lib/auth/useAuth'
import { checkUsernameAvailable, claimUsername, refreshProfileNow, signOut } from '@/lib/auth/auth'

const USERNAME_RE = /^[A-Za-z0-9_]{3,24}$/

// Best-effort suggestion from the account email: local part, characters that
// aren't letters/digits/underscore become underscores, clamped to 24 chars.
function suggestUsername(email: string | null | undefined): string {
  const base = (email ?? '').split('@')[0].replace(/[^A-Za-z0-9_]/g, '_').slice(0, 24)
  return base.length >= 3 ? base : ''
}

export default function UsernamePrompt() {
  const { session, profile, profileLoaded } = useAuth()
  const needed = Boolean(session && profileLoaded && profile && !profile.username)

  const [name, setName] = useState('')
  const [state, setState] = useState<'idle' | 'ok' | 'taken' | 'invalid' | 'check-failed'>('idle')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const suggested = useRef(false)

  useEffect(() => {
    if (needed && !suggested.current) {
      suggested.current = true
      setName(suggestUsername(profile?.email))
    }
    if (!needed) suggested.current = false
  }, [needed, profile?.email])

  // Live availability check (debounced), mirroring the signup field behavior.
  useEffect(() => {
    if (!needed) return
    const candidate = name.trim()
    if (!candidate) return setState('idle')
    if (!USERNAME_RE.test(candidate)) return setState('invalid')
    const t = setTimeout(async () => {
      const available = await checkUsernameAvailable(candidate)
      setState(available ? 'ok' : 'taken')
    }, 350)
    return () => clearTimeout(t)
  }, [name, needed])

  const statusHint = useMemo(() => {
    if (state === 'invalid')
      return '3–24 characters — letters, numbers and underscores only.'
    if (state === 'taken') return 'That username is already taken.'
    if (state === 'check-failed') return 'Could not verify availability — try again.'
    return null
  }, [state])

  if (!needed) return null

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    const candidate = name.trim()
    if (!USERNAME_RE.test(candidate)) return setState('invalid')
    setBusy(true)
    const { error: claimError } = await claimUsername(candidate)
    if (claimError) {
      setBusy(false)
      setError(claimError)
      return
    }
    await refreshProfileNow()
    setBusy(false)
  }

  return createPortal(
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/70 p-4">
      <div
        className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#0e0e12] p-6 shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label="Choose a username"
      >
        <div className="mb-5">
          <h2 className="text-lg font-semibold text-white">Choose a username</h2>
          <p className="mt-1 text-sm text-white/50">
            Your account needs a username before you can continue. You'll use it to sign in
            and it identifies your saved routes.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <label className="block">
            <span className="mb-1.5 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-white/60">
              <User className="h-3.5 w-3.5" /> Username
            </span>
            <div className="relative">
              <input
                type="text"
                autoFocus
                value={name}
                onChange={(e) => {
                  setName(e.target.value)
                  setError(null)
                }}
                placeholder="twisties_fan"
                autoComplete="username"
                className={cn(
                  'w-full rounded-lg border bg-white/5 px-3 py-2.5 pr-9 text-sm text-white placeholder-white/30 outline-none transition-colors focus:border-amber-400/60',
                  state === 'taken' || state === 'invalid'
                    ? 'border-red-400/50'
                    : 'border-white/10'
                )}
              />
              {state === 'ok' && (
                <Check className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-emerald-400" />
              )}
              {(state === 'taken' || state === 'invalid' || state === 'check-failed') && (
                <AlertCircle className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-red-400" />
              )}
            </div>
            {statusHint && <span className="mt-1 block text-xs text-red-400">{statusHint}</span>}
            {error && (
              <span className="mt-1 block text-xs text-red-400">{error}</span>
            )}
          </label>

          <button
            type="submit"
            disabled={busy || state !== 'ok'}
            className={cn(
              'w-full rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-semibold text-black transition-colors',
              busy || state !== 'ok'
                ? 'cursor-not-allowed opacity-60'
                : 'hover:bg-amber-400'
            )}
          >
            {busy ? 'Saving…' : 'Continue'}
          </button>

          <button
            type="button"
            onClick={() => void signOut()}
            className="w-full text-center text-xs text-white/50 transition-colors hover:text-white/80"
          >
            Sign out instead
          </button>
        </form>
      </div>
    </div>,
    document.body
  )
}
